import { Card, CardContent } from '@/components/ui/Card/Card'
import { cn } from '@/utils/helpers'
import { formatPhoneNumber } from '@/utils/phone.utils'
import { SquareCheckBig, Video, LockKeyhole } from 'lucide-react'
import france2030Logo from '@/assets/france2030.png'

type ConsentCardProps = {
  className?: string
  phoneNumber?: string | null
}

type DisclaimerCardProps = {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}

const DisclaimerCard = ({ icon, title, children }: DisclaimerCardProps) => (
  <Card className="flex-1 border border-[var(--border-primary)] bg-background lg:bg-white shadow-none">
    <CardContent className="flex flex-col gap-3 p-5">
      <div className="flex items-center gap-3">
        {icon}
        <h3 className="text-sm leading-tight text-[var(--brand-primary)]">{title}</h3>
      </div>
      <div className="text-xs leading-relaxed font-light">{children}</div>
    </CardContent>
  </Card>
)

export const ConsentCard = ({ className, phoneNumber }: ConsentCardProps) => {
  return (
    <div className={cn('flex flex-col gap-4 lg:flex-row', className)}>
      {/* Card 1 - Recording Info */}
      <DisclaimerCard
        icon={<Video className="size-7 text-[var(--brand-primary)]" />}
        title="Cette visio est enregistrée et transcrite automatiquement"
      >
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-[var(--text-secondary)]">•</span>
            <span>Très bientôt : fini les prises de notes, nous vous enverrons un résumé de votre échange.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[var(--text-secondary)]">•</span>
            <span className="flex flex-1 items-start gap-2">
              <span>
                À terme, certains moments clés de la conversation seront valorisés dans le cadre d&apos;un projet de
                recherche financé par l&apos;État via France 2030.
              </span>
              <img src={france2030Logo} alt="Logo France 2030" className="h-10 w-10 shrink-0 object-contain" />
            </span>
          </li>
        </ul>
      </DisclaimerCard>

      {/* Card 2 - Data Protection */}
      <DisclaimerCard
        icon={<LockKeyhole className="size-5 text-[var(--brand-primary)]" />}
        title="Vos données sont protégées"
      >
        <ul className="space-y-2">
          <li className="flex items-start gap-2">
            <span className="text-[var(--text-secondary)]">•</span>
            <span>L&apos;enregistrement est strictement confidentiel et réservé à un usage My Job Glasses.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-[var(--text-secondary)]">•</span>
            <span>
              Avant que My Job Glasses mette en avant d&apos;éventuels moments clés issus des échanges sur la
              plateforme, vous pourrez gérer ou retirer votre consentement à tout moment depuis votre espace personnel.
            </span>
          </li>
        </ul>
      </DisclaimerCard>

      {/* Card 3 - Consent */}
      <DisclaimerCard
        icon={<SquareCheckBig className="size-7 text-[var(--brand-primary)]" />}
        title="En rejoignant la visio, vous acceptez cet enregistrement"
      >
        <div className="space-y-2">
          <p>Si vous ne souhaitez pas être enregistré, vous pouvez :</p>
          <ul className="space-y-1">
            <li className="flex items-start gap-2">
              <span className="text-[var(--text-secondary)]">•</span>
              <span>
                Revenir sur la conversation et convenir d&apos;un autre mode de rendez-vous avec votre interlocuteur.
              </span>
            </li>
            {phoneNumber && (
              <li className="flex items-start gap-2">
                <span className="text-[var(--text-secondary)]">•</span>
                <span>
                  Le contacter par téléphone au{' '}
                  <a href={`tel:+${phoneNumber}`} className="text-[var(--blue-150)] underline">
                    {formatPhoneNumber(phoneNumber)}
                  </a>
                  .
                </span>
              </li>
            )}
          </ul>
          <p className="pt-1">
            Pour en savoir plus, consultez notre{' '}
            <a
              href="https://www.myjobglasses.com/confidentialite-communaute-connect/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--blue-150)] underline"
            >
              politique de confidentialité
            </a>
            .
          </p>
        </div>
      </DisclaimerCard>
    </div>
  )
}
